import { dayjs } from 'app/utils/dayjs';

const getTimestamp = (value: object): number | undefined => {
  if (value instanceof Date) {
    return value.getTime();
  }

  if (dayjs.isDayjs(value)) {
    return value.valueOf();
  }

  return undefined;
};

const definedEntries = (value: object): [string, unknown][] =>
  Object.entries(value).filter(([, entryValue]) => entryValue !== undefined);

export const isDeepEqual = (left: unknown, right: unknown): boolean => {
  if (left === right) {
    return true;
  }

  if (
    typeof left !== 'object' ||
    typeof right !== 'object' ||
    left === null ||
    right === null
  ) {
    return false;
  }

  const leftTimestamp = getTimestamp(left);
  const rightTimestamp = getTimestamp(right);

  if (leftTimestamp !== undefined || rightTimestamp !== undefined) {
    return leftTimestamp === rightTimestamp;
  }

  if (Array.isArray(left) || Array.isArray(right)) {
    return (
      Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((item, index) => isDeepEqual(item, right[index]))
    );
  }

  const leftEntries = definedEntries(left);
  const rightEntries = definedEntries(right);

  if (leftEntries.length !== rightEntries.length) {
    return false;
  }

  return leftEntries.every(
    ([key, value]) =>
      key in right &&
      isDeepEqual(value, (right as Record<string, unknown>)[key])
  );
};

export const pickUpdatedFields = <T extends object>(
  values: T,
  initialValues: Partial<T>
): Partial<T> =>
  Object.fromEntries(
    Object.entries(values).filter(
      ([key, value]) =>
        !isDeepEqual(value, (initialValues as Record<string, unknown>)[key])
    )
  ) as Partial<T>;
